import { BrandedIdType } from '../../../../customer-creation/model/branded-id-type';

export enum PolicyDocumentFormat {
  JSON = 'JSON',
  HTML = 'HTML',
  PDF = 'PDF',
}

export interface PolicyDocumentQuery {
  policyDocumentName: string;
  majorVersion?: number;
  minorVersion?: number;
  brand?: string;
  preview?: boolean;
  format?: PolicyDocumentFormat;
}

export interface GetConsentChoicesQuery {
  gcid: string;
  fullHistory?: boolean;
  latestOnly?: boolean;
  legalEntity?: string;
  usageId?: string;
  consentGroupId?: string;
}

export enum ConsentRequestValue {
  ACCEPT = 'ACCEPT',
  REJECT = 'REJECT',
}

/**
 * One entry of the array sent to GCDM, for setting consents
 *
 * Rules, as sent by Rui Wang:
 *
 * <ul>
 *   <li> <u>Client always need to send full consentSet</u>: “full” means all combinations of <code>usageId</code>,
 *        <code>purposeId</code> <s>and <code>legalEntityId</code></s> [not needed when using <code>/generate</code>]
 *        to one language &rArr; cartesian product
 *   <li> entire consentSet has same <code>consentDate</code>
 *   <li> if a policy contains several <code>language</code>, client has to send <u>one full consentSet to one
 *        <code>language</code></u> (the language in which the policy was shown to the customer).
 *        <code>language</code> is mandatory.
 *   <li> A client who is sending consents to PolicyStore must use his own <code>touchpointId</code> (must be
 *        documented in IFC!)
 *   <li> <code>touchpointId</code> is a fixed value and never changes (similar to clientId)
 *   <li> <u>Client always need to send consents to latest policyVersion</u>
 *   <li> to know correct values for a consent, a client always need to know the current policyVersion with all the
 *        meta information (<codepolicyId,</code> <code>country</code>, <code>minorVersion</code>,
 *        <code>majorVersion</code>, <code>language</code>, <code>brand</code>, <code>usageId</code>,
 *        <code>purposeId</code>, <code>legalEntityId</code>, <code>consentDate</code>, <code>consent</code> etc.),
 *        which he can fetch via GET policyDocument-Service
 *        (<code>/pm-document-service/api/v1/documents/{policyDocumentName}/{country}</code>)
 *        <ul>
 *          <li> HINT: PM2.0 is offering a publish/subscribe mechanism to inform the client whenever a new policy
 *               (version) was published
 *        </ul>
 *   <li> All mentioned values (e.g. <code>minorVersion</code>, <code>majorVersion</code>, <code>language</code>,
 *        <code>brand</code>, <code>usageId</code>, <code>purposeId</code>, <code>legalEntityId</code>,
 *        <code>country</code>) <u>are never hardcoded on client-side</u>!
 *   <li> <code>expirationDate</code> doesn’t need to be sent, because it will be generated in PM2.0-backend based on
 *        given policy
 *   <li> <code>brand</code> is optional parameter &rarr; client has to send it when a PolicyDocument has one or many
 *        brands configured, else don't send it
 *   <li> <code>binaryDocumentBase64</code> is not needed when client does not send scanned consents
 *   <li> <b>consents with wrong values which does not fit to latest policyVersion will be declined and not stored in
 *        PM2.0</b>.
 * </ul>
 */
export interface SetConsentChoicesRequest {
  binaryDocumentBase64?: never;
  brand?: string;
  consent: ConsentRequestValue;
  consentDate: string;
  expirationDate?: never;
  /** language of the accepted/rejected consent document */
  language: string;
  majorVersion: number;
  minorVersion: number;
  policyId: string;
  purposeId: string;
  usageId: string;
  consentGroupId?: never;
}

export interface PreregistrationRequest {
  brand: BrandedIdType.MINI | BrandedIdType.BMW;
  language: string;
  gcid: string;
  emailAddress: string;
}
